import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

const Wrapper = styled.div`
  .tagInputContainer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    padding: 8px ${({ theme }) => theme.spacing[8]};
    background: ${({ theme }) => theme.colors.semantic.container.white};
    border: 1px solid ${({ theme }) => theme.colors.semantic.outline.default};
    border-radius: ${({ theme }) => theme.radius[4]};
    cursor: text;
    transition: border-color 0.15s ease;

    &:hover,
    &.focused {
      border-color: ${({ theme }) => theme.colors.semantic.text.strong};
    }
  }

  .tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px 4px 10px;
    border-radius: ${({ theme }) => theme.radius[4]};
    background: ${({ theme }) => theme.colors.accent[50]};
    color: ${({ theme }) => theme.colors.accent[700]};
    font-size: 12px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0.1px;
    max-width: 200px;
    white-space: nowrap;
  }

  .tagName {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tagRemove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: ${({ theme }) => theme.radius[4]};
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.accent[500]};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    line-height: 1;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: ${({ theme }) => theme.colors.accent[200]};
      color: ${({ theme }) => theme.colors.accent[900]};
    }
  }

  .searchInput {
    flex: 1;
    min-width: 120px;
    border: none;
    outline: none;
    background: transparent;
    font-size: ${({ theme }) => theme.typography.body.b2.size};
    font-weight: ${({ theme }) => theme.typography.body.b2.weight};
    line-height: ${({ theme }) => theme.typography.body.b2.lineHeight};
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    padding: 2px 0;

    &::placeholder {
      color: ${({ theme }) => theme.colors.semantic.text.strong};
      opacity: 0.5;
    }
  }
`;

const DropdownPortal = styled.div`
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #e3e3e3;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06),
    0px 0px 1px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 8px;
  max-height: 192px;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .dropdownOption {
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    color: #717171;
    transition: background 0.15s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.accent[50]};
    }
  }

  .dropdownEmpty {
    padding: 8px 12px;
    font-size: 13px;
    color: #717171;
    opacity: 0.6;
  }
`;

const BranchTagInput = ({
  selectedBranches,
  availableOptions,
  onAdd,
  onRemove,
  placeholder = "Search and add a branch...",
  labelKey = "name",
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setIsFocused(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();

    // Reposition on scroll of any ancestor (e.g. the modal)
    const scrollableParent = containerRef.current?.closest(
      "[class*='modalContainer']"
    );
    const handleScroll = () => updateDropdownPosition();
    const handleResize = () => updateDropdownPosition();

    if (scrollableParent) {
      scrollableParent.addEventListener("scroll", handleScroll);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      if (scrollableParent) {
        scrollableParent.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, updateDropdownPosition]);

  // Recalc position when tags change (container height changes)
  useEffect(() => {
    if (isOpen) updateDropdownPosition();
  }, [selectedBranches.length, isOpen, updateDropdownPosition]);

  const filteredOptions = availableOptions.filter((option) => {
    const label = option[labelKey] || "";
    return label.toLowerCase().includes(search.toLowerCase());
  });

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
    setIsOpen(true);
  };

  const handleSelect = (option) => {
    onAdd(option);
    setSearch("");
    setIsOpen(false);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Backspace" &&
      search === "" &&
      selectedBranches.length > 0
    ) {
      onRemove(selectedBranches[selectedBranches.length - 1].id);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <div
        ref={containerRef}
        className={`tagInputContainer${isFocused ? " focused" : ""}`}
        onClick={handleContainerClick}
      >
        {selectedBranches.map((branch) => (
          <span key={branch.id} className="tag">
            <span className="tagName">{branch[labelKey]}</span>
            <button
              type="button"
              className="tagRemove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(branch.id);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="searchInput"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={
            selectedBranches.length === 0 ? placeholder : "Add more..."
          }
        />
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <DropdownPortal ref={dropdownRef} style={dropdownStyle}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.id}
                  className="dropdownOption"
                  onClick={() => handleSelect(option)}
                >
                  {option[labelKey]}
                </div>
              ))
            ) : (
              <div className="dropdownEmpty">
                {search ? "No results found" : "All branches assigned"}
              </div>
            )}
          </DropdownPortal>,
          document.body
        )}
    </Wrapper>
  );
};

export default BranchTagInput;
